import request, { IWorkspaceResponse } from '/@/api/http/request'
import { CURRENT_CONFIG } from '/@/api/http/config'
import { ELocalStorageKey } from '/@/types/enums'

const HTTP_PREFIX = '/manage/api/v1/fh2'

function encodeMediaUrlForProxy (remoteUrl: string): string {
  try {
    return btoa(encodeURIComponent(remoteUrl))
  } catch {
    return ''
  }
}

/** Same-origin proxy for FH2 CDN thumbnails (avoids Referer 403 in img tags). */
export function buildFh2MediaProxyUrl (remoteUrl: string): string {
  const trimmed = remoteUrl?.trim()
  if (!trimmed) return ''
  const base = (CURRENT_CONFIG.baseURL || '').replace(/\/$/, '')
  const params = new URLSearchParams()
  const encoded = encodeMediaUrlForProxy(trimmed)
  if (encoded) {
    params.set('u', encoded)
  } else {
    params.set('url', trimmed)
  }
  const token = localStorage.getItem(ELocalStorageKey.Token)
  if (token) {
    params.set(ELocalStorageKey.Token, token)
  }
  return `${base}/manage/api/v1/fh2/media-proxy?${params.toString()}`
}

/** Proxied download (same CDN Referer fix as preview). */
export function buildFh2MediaDownloadUrl (remoteUrl: string, filename?: string): string {
  const proxy = buildFh2MediaProxyUrl(remoteUrl)
  if (!proxy) return ''
  const extra = new URLSearchParams({ download: '1' })
  const name = filename?.trim()
  if (name) {
    extra.set('filename', name)
  }
  return `${proxy}&${extra.toString()}`
}

export interface Fh2WorkspaceBinding {
  id?: number,
  workspace_id?: string,
  workspaceId?: string,
  fh2_org_uuid?: string,
  fh2OrgUuid?: string,
  fh2_org_name?: string,
  fh2OrgName?: string,
  fh2_user_token?: string,
  fh2UserToken?: string,
  enabled?: boolean,
  sync_username?: string,
  syncUsername?: string,
  last_sync_at?: number,
  lastSyncAt?: number,
  create_time?: number,
  update_time?: number,
}

export interface Fh2TaskAlbumMap {
  id?: number,
  workspace_id?: string,
  fh2_project_uuid?: string,
  fh2ProjectUuid?: string,
  fh2_task_uuid?: string,
  fh2TaskUuid?: string,
  fh2_task_name?: string,
  fh2TaskName?: string,
  irrigation_album_id?: number,
  irrigationAlbumId?: number,
  sync_status?: string,
  syncStatus?: string,
  synced_media_count?: number,
  syncedMediaCount?: number,
  last_error?: string,
  lastError?: string,
  last_sync_at?: number,
  lastSyncAt?: number,
  update_time?: number,
}

export interface Fh2BindingSaveBody {
  id?: number,
  fh2_org_uuid: string,
  fh2_org_name?: string,
  fh2_user_token: string,
  enabled?: boolean,
  sync_username?: string,
}

export interface Fh2Status {
  base_url_configured?: boolean,
  sync_service_active?: boolean,
}

export const getFh2Status = async function (): Promise<IWorkspaceResponse<Fh2Status>> {
  const result = await request.get(`${HTTP_PREFIX}/status`)
  return result.data
}

/** 当前 workspace 唯一的司空组织配置 */
export const getFh2Binding = async function (): Promise<IWorkspaceResponse<Fh2WorkspaceBinding | null>> {
  const result = await request.get(`${HTTP_PREFIX}/binding`)
  return result.data
}

export const saveFh2Binding = async function (body: Fh2BindingSaveBody): Promise<IWorkspaceResponse<number>> {
  const result = await request.post(`${HTTP_PREFIX}/binding`, body)
  return result.data
}

export const clearFh2Binding = async function (): Promise<IWorkspaceResponse<boolean>> {
  const result = await request.delete(`${HTTP_PREFIX}/binding`)
  return result.data
}

export const syncFh2BindingNow = async function (): Promise<IWorkspaceResponse<string>> {
  const result = await request.post(`${HTTP_PREFIX}/binding/sync-now`)
  return result.data
}

export interface Fh2Organization {
  org_uuid?: string,
  orgUuid?: string,
  org_name?: string,
  orgName?: string,
  name_source?: string,
  nameSource?: string,
}

export interface Fh2Project {
  uuid?: string,
  name?: string,
  org_uuid?: string,
  orgUuid?: string,
}

export interface Fh2Wayline {
  id?: string,
  name?: string,
  template_types?: string[],
  templateTypes?: string[],
  update_time?: number,
  updateTime?: number,
  device_model_key?: string,
  deviceModelKey?: string,
  download_url?: string,
  downloadUrl?: string,
}

export interface Fh2ProjectDevice {
  gateway_sn?: string,
  gatewaySn?: string,
  gateway_name?: string,
  gatewayName?: string,
  gateway_callsign?: string,
  gatewayCallsign?: string,
  gateway_online?: boolean,
  gatewayOnline?: boolean,
  drone_sn?: string,
  droneSn?: string,
  drone_name?: string,
  droneName?: string,
  drone_callsign?: string,
  droneCallsign?: string,
  drone_online?: boolean,
  droneOnline?: boolean,
}

export interface Fh2FlightTask {
  task_uuid?: string,
  taskUuid?: string,
  name?: string,
  status?: string,
  task_type?: string,
  taskType?: string,
  sn?: string,
  landing_dock_sn?: string,
  landingDockSn?: string,
  wayline_id?: string,
  waylineId?: string,
  wayline_uuid?: string,
  waylineUuid?: string,
  wayline_name?: string,
  waylineName?: string,
  begin_at?: number,
  beginAt?: number,
  end_at?: number,
  endAt?: number,
  run_at?: number,
  runAt?: number,
  completed_at?: number,
  completedAt?: number,
  media_upload_status?: string,
  mediaUploadStatus?: string,
  current_waypoint_index?: number,
  currentWaypointIndex?: number,
  total_waypoints?: number,
  totalWaypoints?: number,
  folder_id?: number,
  folderId?: number,
  create_at?: number,
  createAt?: number,
}

export interface Fh2Model {
  id?: number,
  name?: string,
  file_type?: string,
  fileType?: string,
  show_on_map?: boolean,
  showOnMap?: boolean,
  size?: number,
  update_at?: number,
  updateAt?: number,
  create_at?: number,
  createAt?: number,
  user_name?: string,
  userName?: string,
  url?: string,
  preview_url?: string,
  previewUrl?: string,
}

export const resolveFh2Organization = async function (
  params?: { fh2_user_token?: string, org_uuid?: string }
): Promise<IWorkspaceResponse<Fh2Organization>> {
  const result = await request.get(`${HTTP_PREFIX}/organization/resolve`, { params })
  return result.data
}

export interface Fh2ShootPosition {
  abs_alt?: number,
  absAlt?: number,
  lat?: number,
  lng?: number,
  rel_alt?: number,
  relAlt?: number,
}

export interface Fh2WaylineInfo {
  wayline_template_types?: number[],
  waylineTemplateTypes?: number[],
  smart_oblique_on?: boolean,
  smartObliqueOn?: boolean,
  origin_wayline_uuid?: string,
  originWaylineUuid?: string,
}

export interface Fh2MediaLibraryItem {
  org_uuid?: string,
  orgUuid?: string,
  project_uuid?: string,
  projectUuid?: string,
  project_name?: string,
  projectName?: string,
  task_uuid?: string,
  taskUuid?: string,
  task_name?: string,
  taskName?: string,
  task_status?: string,
  taskStatus?: string,
  media_uuid?: string,
  mediaUuid?: string,
  name?: string,
  file_type?: string,
  fileType?: string,
  suffix?: string,
  size?: number,
  preview_url?: string,
  previewUrl?: string,
  original_url?: string,
  originalUrl?: string,
  create_at?: string,
  createAt?: string,
  shoot_position?: Fh2ShootPosition,
  shootPosition?: Fh2ShootPosition,
  wayline_info?: Fh2WaylineInfo,
  waylineInfo?: Fh2WaylineInfo,
}

export interface Fh2OrganizationMediaLibrary {
  org_uuid?: string,
  orgUuid?: string,
  parent_id?: string,
  parentId?: string,
  project_count?: number,
  projectCount?: number,
  task_count?: number,
  taskCount?: number,
  media_count?: number,
  mediaCount?: number,
  truncated?: boolean,
  page?: number,
  pageSize?: number,
  page_size?: number,
  total?: number,
  count?: number,
  has_more?: boolean,
  hasMore?: boolean,
  folder_count?: number,
  folderCount?: number,
  file_count?: number,
  fileCount?: number,
  projects?: Fh2Project[],
  media_list?: Fh2MediaLibraryItem[],
  mediaList?: Fh2MediaLibraryItem[],
}

export const listFh2OrganizationProjects = async function (
  orgUuid: string,
  params?: { page?: number, page_size?: number }
): Promise<IWorkspaceResponse<Fh2Project[]>> {
  const result = await request.get(`${HTTP_PREFIX}/organizations/${encodeURIComponent(orgUuid)}/projects`, { params })
  return result.data
}

export const browseFh2OrganizationMediaLibrary = async function (
  orgUuid: string,
  params: {
    project_uuid: string,
    parent_id?: string,
    page?: number,
    page_size?: number,
    max_projects?: number,
    max_tasks_per_project?: number,
    max_total_media?: number,
  }
): Promise<IWorkspaceResponse<Fh2OrganizationMediaLibrary>> {
  const result = await request.get(`${HTTP_PREFIX}/organizations/${encodeURIComponent(orgUuid)}/media-library`, { params })
  return result.data
}

export const listFh2ProjectWaylines = async function (
  projectUuid: string
): Promise<IWorkspaceResponse<Fh2Wayline[]>> {
  const result = await request.get(`${HTTP_PREFIX}/projects/${encodeURIComponent(projectUuid)}/waylines`)
  return result.data
}

export const getFh2ProjectWayline = async function (
  projectUuid: string,
  waylineId: string
): Promise<IWorkspaceResponse<Fh2Wayline>> {
  const result = await request.get(
    `${HTTP_PREFIX}/projects/${encodeURIComponent(projectUuid)}/waylines/${encodeURIComponent(waylineId)}`
  )
  return result.data
}

function fh2MediaProxyPath (remoteUrl: string): string {
  const trimmed = remoteUrl?.trim()
  if (!trimmed) return ''
  const params = new URLSearchParams()
  const encoded = encodeMediaUrlForProxy(trimmed)
  if (encoded) {
    params.set('u', encoded)
  } else {
    params.set('url', trimmed)
  }
  const token = localStorage.getItem(ELocalStorageKey.Token)
  if (token) {
    params.set(ELocalStorageKey.Token, token)
  }
  return `${HTTP_PREFIX}/media-proxy?${params.toString()}`
}

/** 经同源代理拉取司空航线 KMZ，供地图预览解析。 */
export async function downloadFh2WaylineKmz (
  projectUuid: string,
  waylineId: string
): Promise<Blob | null> {
  const res = await getFh2ProjectWayline(projectUuid, waylineId)
  const remote = res.data?.download_url ?? res.data?.downloadUrl
  if (res.code !== 0 || !remote) {
    return null
  }
  const path = fh2MediaProxyPath(remote)
  if (!path) return null
  const result = await request.get(path, { responseType: 'blob' })
  const blob = result.data as Blob
  if (blob?.type === 'application/json') {
    return null
  }
  return blob instanceof Blob && blob.size > 0 ? blob : null
}

export const listFh2ProjectDevices = async function (
  projectUuid: string
): Promise<IWorkspaceResponse<Fh2ProjectDevice[]>> {
  const result = await request.get(
    `${HTTP_PREFIX}/projects/${encodeURIComponent(projectUuid)}/devices`
  )
  return result.data
}

export const listFh2ProjectFlightTasks = async function (
  projectUuid: string,
  params?: {
    status?: string,
    sn?: string,
    name?: string,
    begin_at?: number,
    end_at?: number,
    task_type?: string,
    flight_task_type?: string,
    max_tasks?: number,
    lookback_days?: number,
  }
): Promise<IWorkspaceResponse<Fh2FlightTask[]>> {
  const result = await request.get(
    `${HTTP_PREFIX}/projects/${encodeURIComponent(projectUuid)}/flight-tasks`,
    { params }
  )
  return result.data
}

export const getFh2ProjectFlightTask = async function (
  projectUuid: string,
  taskUuid: string
): Promise<IWorkspaceResponse<Fh2FlightTask>> {
  const result = await request.get(
    `${HTTP_PREFIX}/projects/${encodeURIComponent(projectUuid)}/flight-tasks/${encodeURIComponent(taskUuid)}`
  )
  return result.data
}

export const listFh2ProjectFlightTaskMedia = async function (
  projectUuid: string,
  taskUuid: string,
  params: {
    org_uuid: string,
    folder_id?: number,
    task_name?: string,
    images_only?: boolean,
  }
): Promise<IWorkspaceResponse<Fh2MediaLibraryItem[]>> {
  const result = await request.get(
    `${HTTP_PREFIX}/projects/${encodeURIComponent(projectUuid)}/flight-tasks/${encodeURIComponent(taskUuid)}/media`,
    { params }
  )
  return result.data
}

export const listFh2ProjectModels = async function (
  projectUuid: string,
  params?: { file_type?: string }
): Promise<IWorkspaceResponse<Fh2Model[]>> {
  const result = await request.get(`${HTTP_PREFIX}/projects/${encodeURIComponent(projectUuid)}/models`, { params })
  return result.data
}

export const getFh2ProjectModel = async function (
  projectUuid: string,
  modelId: string | number
): Promise<IWorkspaceResponse<Fh2Model>> {
  const result = await request.get(
    `${HTTP_PREFIX}/projects/${encodeURIComponent(projectUuid)}/models/${encodeURIComponent(String(modelId))}`
  )
  return result.data
}

export const listFh2TaskMaps = async function (syncStatus?: string): Promise<IWorkspaceResponse<Fh2TaskAlbumMap[]>> {
  const params = syncStatus ? { sync_status: syncStatus } : {}
  const result = await request.get(`${HTTP_PREFIX}/task-maps`, { params })
  return result.data
}

export interface Fh2MediaIrrigationSyncItem {
  media_uuid: string,
  name?: string,
  original_url: string,
  /** FH2 file list size (bytes) */
  size?: number,
  /** FH2 create_at, e.g. 2026-04-23T05:26:57Z */
  create_at?: string,
  /** FH2 shoot_position.lat */
  center_lat?: number,
  /** FH2 shoot_position.lng */
  center_lon?: number,
  shoot_position?: Fh2ShootPosition,
}

export interface Fh2MediaIrrigationSyncBody {
  /** 与浏览媒体库时使用的组织一致 */
  org_uuid: string,
  project_uuid: string,
  /** 飞行任务媒体同步时传 FH2 task uuid */
  task_uuid?: string,
  task_name?: string,
  /** 追加到已有 media_sync_task 时使用 */
  task_id?: string,
  enqueue_complete?: boolean,
  album_id?: number,
  album_name?: string,
  album_type?: string,
  create_album?: boolean,
  sync_username?: string,
  /** 勾选目录全部图片时传 true，由后端扫描 Data Hub 并入队 */
  select_all_directory?: boolean,
  /** 计划 folder_id，与 parent_id 二选一；优先 folder_id */
  folder_id?: number,
  parent_id?: string,
  /** 默认 true：仅同步图片文件 */
  images_only?: boolean,
  items?: Fh2MediaIrrigationSyncItem[],
}

export interface Fh2MediaIrrigationSyncResult {
  task_id?: string,
  taskId?: string,
  status?: string,
  album_id?: number,
  albumId?: number,
  total_count?: number,
  totalCount?: number,
  unsynced_count?: number,
  unsyncedCount?: number,
  success_count?: number,
  successCount?: number,
  failed_count?: number,
  failedCount?: number,
  errors?: string[],
}

/** 与 workspace 批量同步一致：单次请求入队条数上限 */
export const FH2_IRRIGATION_SYNC_CHUNK = 500

export const syncFh2MediaToIrrigation = async function (
  body: Fh2MediaIrrigationSyncBody
): Promise<IWorkspaceResponse<Fh2MediaIrrigationSyncResult>> {
  const result = await request.post(`${HTTP_PREFIX}/media/sync-to-irrigation`, body)
  return result.data
}

function pickFh2SyncTaskId (data: Fh2MediaIrrigationSyncResult | undefined | null): string | undefined {
  if (!data) return undefined
  return data.task_id ?? data.taskId
}

/**
 * 创建同一异步同步任务；超过 {@link FH2_IRRIGATION_SYNC_CHUNK} 时分批追加明细。
 */
export const syncFh2MediaToIrrigationChunked = async function (
  body: Fh2MediaIrrigationSyncBody,
  chunkSize = FH2_IRRIGATION_SYNC_CHUNK
): Promise<IWorkspaceResponse<Fh2MediaIrrigationSyncResult>> {
  if (body.select_all_directory) {
    return syncFh2MediaToIrrigation({ ...body, enqueue_complete: body.enqueue_complete ?? true, items: undefined })
  }
  const items = body.items
  if (!items?.length) {
    return { code: -1, data: {} as Fh2MediaIrrigationSyncResult, message: 'items 为空' }
  }
  if (items.length <= chunkSize) {
    return syncFh2MediaToIrrigation({ ...body, enqueue_complete: body.enqueue_complete ?? true })
  }

  const first = await syncFh2MediaToIrrigation({
    ...body,
    items: items.slice(0, chunkSize),
    enqueue_complete: false,
  })
  if (first.code !== 0 || first.data == null) {
    return first
  }
  const taskId = pickFh2SyncTaskId(first.data)
  if (!taskId) {
    return { code: -1, message: '未返回 task_id，无法继续分批追加', data: first.data }
  }

  let last = first.data
  for (let i = chunkSize; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize)
    const isLast = i + chunkSize >= items.length
    const res = await syncFh2MediaToIrrigation({
      project_uuid: body.project_uuid,
      task_id: taskId,
      album_id: body.album_id,
      album_name: body.album_name,
      album_type: body.album_type,
      create_album: body.create_album,
      sync_username: body.sync_username,
      items: chunk,
      enqueue_complete: isLast,
    })
    if (res.code !== 0 || res.data == null) {
      return res
    }
    last = res.data
  }

  return {
    code: 0,
    message: 'ok',
    data: {
      ...last,
      task_id: pickFh2SyncTaskId(last) ?? taskId,
      total_count: items.length,
      totalCount: items.length,
    },
  }
}
